import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import StatusBadge from '../components/StatusBadge'
import Icon from '../components/Icon'
import {
  getGoalEventsByGoal,
  deleteGoalEvent,
  type GoalEvent,
} from '../../services/goalEvents'
import { getGoal } from '../../services/goals'
import { Goal, Permission } from '../../types'
import { useAuth } from '../../contexts/AuthContext'

const GoalEventsList: React.FC = () => {
  const { goalId } = useParams<{ goalId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [goalEvents, setGoalEvents] = useState<GoalEvent[]>([])
  const [goal, setGoal] = useState<Goal | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canManageGoalEvents = user?.permissions.includes(
    Permission.MANAGE_GOALS,
  )

  useEffect(() => {
    if (goalId) {
      fetchGoalAndEvents()
    }
  }, [goalId])

  const fetchGoalAndEvents = async () => {
    if (!goalId) return

    setLoading(true)
    setError(null)
    try {
      const [goalRes, eventsRes] = await Promise.all([
        getGoal(goalId),
        getGoalEventsByGoal(goalId),
      ])
      setGoal(goalRes.data)
      setGoalEvents(eventsRes)
    } catch (err: any) {
      console.error('Error fetching goal or events:', err)
      setError(err.response?.data?.error || 'Failed to fetch data')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Goal Achieved':
        return 'bg-green-100 text-green-800'
      case 'Making Sufficient Progress':
        return 'bg-blue-100 text-blue-800'
      case 'Not Making Sufficient Progress':
        return 'bg-red-100 text-red-800'
      case 'Not Yet Introduced':
        return 'bg-yellow-100 text-yellow-800'
      case 'Not Applicable To My Subject Area':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const columns = useMemo<ColumnDef<GoalEvent>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {formatDate(info.getValue() as string)}
          </div>
        ),
      },
      {
        accessorKey: 'teacher',
        header: 'Teacher',
        cell: (info) => {
          const teacher = info.getValue() as GoalEvent['teacher']
          if (typeof teacher === 'string') {
            return <div className="text-sm text-gray-900">Unknown Teacher</div>
          }

          return (
            <div className="text-sm text-gray-900">
              {teacher.firstName} {teacher.lastName}
            </div>
          )
        },
      },
      {
        accessorKey: 'period',
        header: 'Time Period',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          const status = info.getValue() as string
          return (
            <StatusBadge colorClass={getStatusColor(status)}>
              {status}
            </StatusBadge>
          )
        },
      },
      {
        accessorKey: 'levelOfIndependence',
        header: 'Independence Level',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          if (!canManageGoalEvents) {
            return null
          }

          return (
            <div className="text-right text-sm font-medium flex">
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/goals/${goalId}/goal-events/${row.original._id}`)
                }}
                variant="link"
                className="mr-3"
              >
                Edit
              </Button>
              <Button
                onClick={async (e) => {
                  e.stopPropagation()
                  const confirmed = window.confirm(
                    'Are you sure you want to delete this goal event? This action cannot be undone.',
                  )
                  if (!confirmed) {
                    return
                  }

                  try {
                    await deleteGoalEvent(row.original._id)
                    await fetchGoalAndEvents()
                  } catch (err: any) {
                    console.error('Error deleting goal event:', err)
                    setError(
                      err.response?.data?.error ||
                        'Failed to delete goal event',
                    )
                  }
                }}
                variant="link"
                className="text-red-600 hover:text-red-900"
              >
                Delete
              </Button>
            </div>
          )
        },
      },
    ],
    [canManageGoalEvents, goalId, navigate],
  )

  return (
    <div className="space-y-6">
      <div>
        <Button
          onClick={() => navigate(-1)}
          variant="link"
          className="mb-4 flex items-center gap-2"
        >
          ← Back
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Progress Reports</h1>
        {goal && (
          <>
            <p className="mt-2 text-gray-600">
              <strong>Goal Description:</strong> {goal.description}
            </p>
            <p className="mt-2 text-gray-600">
              <strong>Category:</strong> {goal.category}
            </p>
            <p className="mt-2 text-gray-600">
              <strong>Area:</strong> {goal.area}
            </p>
          </>
        )}
        {canManageGoalEvents && (
          <div className="flex justify-end">
            <Button
              onClick={() => navigate(`/goals/${goalId}/goal-events/new`)}
              variant="primary"
              className="mt-4 flex items-center gap-2"
            >
              <Icon type="plus" />
              Create Progress Report
            </Button>
          </div>
        )}
      </div>

      <DataTable
        data={goalEvents}
        columns={columns}
        searchPlaceholder="Search goal events..."
        emptyStateTitle="No goal events found"
        emptyStateDescription="No goal events have been logged for this goal yet."
        loading={loading}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
    </div>
  )
}

export default GoalEventsList
